import {
  MATCH_STATUS_FINISHED,
  MATCH_STATUS_REVIEWED,
  MATCH_STATUS_NOT_STARTED,
  MATCH_STATUS_PENDING_DATE,
  AW_PLAYER_A,
  AW_PLAYER_B,
  AW_BOTH_PLAYERS,
  AW_REMOVE,
  gamesTemplate_PA3,
  gamesTemplate_BO5,
  gamesTemplate_BO1,
} from "../utils/constants";

const GAMES_FORMAT_THRESHOLDS = {
  PA3: { maxGames: 3, threshold: 3 },
  BO5: { maxGames: 5, threshold: 3 },
  BO1: { maxGames: 1, threshold: 1 },
};

const initializeGames = (games, template) => {
  const toRet = template.map((t) => ({ ...t }));

  if (!games) return toRet;

  for (let index = 0; index < toRet.length; index++) {
    for (const game of games) {
      if (toRet[index].sequence === game.sequence) {
        toRet[index].gameAoe2Id = game.gameAoe2Id;
        toRet[index].gameWinner = game.gameWinner;
        toRet[index].gameCivA = game.gameCivA;
        toRet[index].gameCivB = game.gameCivB;
        toRet[index].map = game.map;
        toRet[index].gameRec = game.gameRec;
        break;
      }
    }
  }

  return toRet;
};

const getTemplate = (gamesFormat) => {
  if (gamesFormat === "PA3") return gamesTemplate_PA3;
  if (gamesFormat === "BO5") return gamesTemplate_BO5;
  if (gamesFormat === "BO1") return gamesTemplate_BO1;
  return [];
};

const computeResults = (games, playerAid) => {
  const resultA = games.filter((g) => g.gameWinner === playerAid).length;
  const resultB = games.filter(
    (g) => g.gameWinner && g.gameWinner !== playerAid
  ).length;
  return { matchResultA: resultA, matchResultB: resultB };
};

const computeStatus = (gamesFormat, matchResultA, matchResultB, currentStatus, matchDate) => {
  const fmt = GAMES_FORMAT_THRESHOLDS[gamesFormat];
  if (!fmt) return currentStatus;

  if (currentStatus === MATCH_STATUS_REVIEWED) return currentStatus;

  if (matchResultA + matchResultB >= fmt.threshold) {
    return MATCH_STATUS_FINISHED;
  }

  if (matchDate && currentStatus === MATCH_STATUS_PENDING_DATE) {
    return MATCH_STATUS_NOT_STARTED;
  }

  return currentStatus;
};

const computeChangesPending = (state) => {
  const match = state._match;
  if (!match) return false;

  const gs = state.gameStep;
  const currentGame = state.games[gs];
  const originalGame = match.games?.[gs];

  if (
    currentGame?.gameCivA !== originalGame?.gameCivA ||
    currentGame?.gameCivB !== originalGame?.gameCivB ||
    currentGame?.map !== originalGame?.map ||
    currentGame?.gameWinner !== originalGame?.gameWinner
  ) {
    return true;
  }

  if (state.mapDraft !== (match.mapDraft || "")) return true;
  if (state.civDraft !== (match.civDraft || "")) return true;
  if (state.matchDate !== match.matchDate) return true;

  return false;
};

const initFromMatch = (match) => {
  const template = getTemplate(match.gamesFormat);
  const games = initializeGames(match.games, template);
  const { matchResultA, matchResultB } = computeResults(games, match.playerAid);

  return {
    _match: match,
    games,
    gameStep: 0,
    matchDate: match.matchDate || null,
    showDateTimePicker: !match.matchDate,
    mapDraft: match.mapDraft || "",
    civDraft: match.civDraft || "",
    playerAaw: match.playerAaw || "",
    playerBaw: match.playerBaw || "",
    matchResultA,
    matchResultB,
    matchStatus: match.status || null,
    changesPending: false,
  };
};

export const initialState = {
  _match: null,
  games: [],
  gameStep: 0,
  matchDate: null,
  showDateTimePicker: true,
  mapDraft: "",
  civDraft: "",
  playerAaw: "",
  playerBaw: "",
  matchResultA: 0,
  matchResultB: 0,
  matchStatus: null,
  changesPending: false,
};

export const matchFormReducer = (state, action) => {
  switch (action.type) {
    case "INIT_MATCH": {
      return initFromMatch(action.payload);
    }

    case "SET_GAME_STEP": {
      return { ...state, gameStep: action.payload };
    }

    case "SET_GAME_FIELD": {
      const { step, field, value } = action.payload;
      const games = state.games.map((g, i) =>
        i === step ? { ...g, [field]: value } : g
      );

      let nextState = { ...state, games };

      if (field === "gameWinner") {
        const { matchResultA, matchResultB } = computeResults(
          games,
          state._match?.playerAid
        );
        nextState.matchResultA = matchResultA;
        nextState.matchResultB = matchResultB;
        nextState.matchStatus = computeStatus(
          state._match?.gamesFormat,
          matchResultA,
          matchResultB,
          state.matchStatus,
          state.matchDate
        );
      }

      nextState.changesPending = computeChangesPending(nextState);
      return nextState;
    }

    case "SET_MATCH_FIELD": {
      const { field, value } = action.payload;
      const nextState = { ...state, [field]: value };

      if (field === "matchDate") {
        nextState.showDateTimePicker = false;
        nextState.matchStatus = computeStatus(
          state._match?.gamesFormat,
          state.matchResultA,
          state.matchResultB,
          state.matchStatus,
          value
        );
      }

      nextState.changesPending = computeChangesPending(nextState);
      return nextState;
    }

    case "SET_ADMIN_WIN": {
      const option = action.payload;
      const playerAid = state._match?.playerAid;

      if (option === AW_PLAYER_A) {
        return {
          ...state,
          playerAaw: "AW",
          playerBaw: "",
          matchResultA: 3,
          matchResultB: 0,
          matchStatus: MATCH_STATUS_REVIEWED,
          changesPending: true,
        };
      }
      if (option === AW_PLAYER_B) {
        return {
          ...state,
          playerAaw: "",
          playerBaw: "AW",
          matchResultA: 0,
          matchResultB: 3,
          matchStatus: MATCH_STATUS_REVIEWED,
          changesPending: true,
        };
      }
      if (option === AW_BOTH_PLAYERS) {
        return {
          ...state,
          playerAaw: "AL",
          playerBaw: "AL",
          matchResultA: 0,
          matchResultB: 0,
          matchStatus: MATCH_STATUS_REVIEWED,
          changesPending: true,
        };
      }
      if (option === AW_REMOVE) {
        return {
          ...state,
          playerAaw: "",
          playerBaw: "",
          matchResultA: 0,
          matchResultB: 0,
          matchStatus: MATCH_STATUS_NOT_STARTED,
          changesPending: true,
        };
      }
      return state;
    }

    case "DELETE_RESULTS": {
      const games = state.games.map((g) => ({ ...g, gameWinner: null }));
      return {
        ...state,
        games,
        matchResultA: 0,
        matchResultB: 0,
        changesPending: true,
      };
    }

    case "MARK_REVIEWED": {
      return {
        ...state,
        matchStatus: MATCH_STATUS_REVIEWED,
        changesPending: true,
      };
    }

    case "PUBLISH_ACK": {
      return {
        ...state,
        _match: { ...state._match, ...action.payload },
        changesPending: false,
      };
    }

    default:
      return state;
  }
};
